import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import GridViewIcon from '@mui/icons-material/GridView';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const SectionContainer = styled(Box)({
  padding: '140px 60px',
  background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f0f 100%)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '800px',
    height: '400px',
    background: 'radial-gradient(ellipse, rgba(212, 175, 55, 0.08) 0%, transparent 70%)',
    pointerEvents: 'none'
  },
  '@media (max-width: 1024px)': {
    padding: '100px 32px'
  },
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const ContentWrapper = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto',
  position: 'relative',
  zIndex: 10
});

const SectionHeader = styled(Box)({
  textAlign: 'center',
  marginBottom: '72px',
  animation: `${fadeInUp} 0.8s ease-out`
});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 24px',
  background: 'rgba(212, 175, 55, 0.1)',
  border: '1px solid rgba(212, 175, 55, 0.2)',
  borderRadius: '50px',
  marginBottom: '24px'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: '#d4af37',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: 'clamp(36px, 5vw, 56px)',
  fontWeight: 400,
  color: '#ffffff',
  lineHeight: 1.2,
  letterSpacing: '-1px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #d4af37 0%, #f4e4bc 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
});

const PhotosGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '24px',
  '@media (max-width: 900px)': {
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  '@media (max-width: 500px)': {
    gridTemplateColumns: '1fr'
  }
});

const PhotoCard = styled(Box)(({ index }) => ({
  position: 'relative',
  overflow: 'hidden',
  cursor: 'pointer',
  borderRadius: '20px',
  aspectRatio: '1',
  background: 'rgba(255, 255, 255, 0.05)',
  animation: `${fadeInUp} 0.8s ease-out`,
  animationDelay: `${index * 0.1}s`,
  animationFillMode: 'backwards',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
  '&:hover': {
    '& img': {
      transform: 'scale(1.1)'
    },
    '& .photo-overlay': {
      opacity: 1
    }
  }
}));

const PhotoImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
});

const PhotoOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.8) 100%)',
  opacity: 0,
  transition: 'opacity 0.4s ease',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: '28px'
});

const OverlayIcon = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #d4af37 0%, #f4e4bc 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 8px 30px rgba(212, 175, 55, 0.4)',
  '& svg': {
    fontSize: '26px',
    color: '#1a1a2e'
  }
});

const PhotoCaption = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '16px',
  fontWeight: 500,
  color: '#ffffff'
});

const ViewAllWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '56px',
  animation: `${fadeInUp} 0.8s ease-out`,
  animationDelay: '0.3s',
  animationFillMode: 'backwards'
});

const ViewAllButton = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '12px',
  padding: '18px 40px',
  background: 'transparent',
  border: '2px solid rgba(212, 175, 55, 0.5)',
  borderRadius: '60px',
  cursor: 'pointer',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #d4af37 0%, #f4e4bc 100%)',
    borderColor: '#d4af37',
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 40px rgba(212, 175, 55, 0.3)',
    '& .btn-text': {
      color: '#1a1a2e'
    },
    '& .btn-icon': {
      color: '#1a1a2e',
      transform: 'translateX(4px)'
    },
    '& .grid-icon': {
      color: '#1a1a2e'
    }
  }
});

const ViewAllText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 600,
  color: '#d4af37',
  transition: 'color 0.3s ease'
});

const ViewAllIcon = styled(ArrowForwardIcon)({
  fontSize: '20px',
  color: '#d4af37',
  transition: 'all 0.3s ease'
});

const PhotoCount = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  background: 'rgba(212, 175, 55, 0.1)',
  borderRadius: '30px',
  marginLeft: '8px'
});

const CountText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 600,
  color: '#d4af37'
});

// Single Image Lightbox Modal
const LightboxModal = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.95)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  animation: `${fadeIn} 0.3s ease`,
  backdropFilter: 'blur(10px)'
});

const LightboxImage = styled('img')({
  maxWidth: '90vw',
  maxHeight: '85vh',
  objectFit: 'contain',
  borderRadius: '12px',
  boxShadow: '0 30px 100px rgba(0, 0, 0, 0.5)'
});

// Gallery Modal for View All
const GalleryModalOverlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.95)',
  zIndex: 2000,
  animation: `${fadeIn} 0.3s ease`,
  backdropFilter: 'blur(20px)',
  overflowY: 'auto'
});

const GalleryModalContent = styled(Box)({
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '100px 40px 60px',
  animation: `${scaleIn} 0.4s ease-out`,
  '@media (max-width: 768px)': {
    padding: '80px 20px 40px'
  }
});

const GalleryModalHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '48px'
});

const GalleryModalTitle = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: 'clamp(28px, 4vw, 40px)',
  fontWeight: 400,
  color: '#ffffff',
  '& span': {
    background: 'linear-gradient(135deg, #d4af37 0%, #f4e4bc 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  }
});

const GalleryModalSubtitle = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.5)',
  marginTop: '8px'
});

const GalleryGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '20px',
  '@media (max-width: 900px)': {
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  '@media (max-width: 500px)': {
    gridTemplateColumns: '1fr',
    gap: '16px'
  }
});

const GalleryItem = styled(Box)(({ index }) => ({
  position: 'relative',
  overflow: 'hidden',
  cursor: 'pointer',
  borderRadius: '16px',
  aspectRatio: '1',
  animation: `${fadeInUp} 0.5s ease-out`,
  animationDelay: `${index * 0.05}s`,
  animationFillMode: 'backwards',
  '&:hover': {
    '& img': {
      transform: 'scale(1.08)'
    },
    '& .gallery-overlay': {
      opacity: 1
    }
  }
}));

const GalleryItemImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 0.5s ease'
});

const GalleryItemOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: '32px',
    color: '#d4af37'
  }
});

const NavButton = styled(IconButton)({
  position: 'absolute',
  width: '56px',
  height: '56px',
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  color: '#ffffff',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #d4af37 0%, #f4e4bc 100%)',
    borderColor: '#d4af37',
    color: '#1a1a2e'
  }
});

const CloseButton = styled(IconButton)({
  position: 'fixed',
  top: '28px',
  right: '28px',
  width: '56px',
  height: '56px',
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  color: '#ffffff',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',
  zIndex: 10,
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.2)'
  }
});

const ImageCounter = styled(Typography)({
  position: 'absolute',
  bottom: '36px',
  left: '50%',
  transform: 'translateX(-50%)',
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.6)',
  padding: '10px 24px',
  background: 'rgba(255, 255, 255, 0.1)',
  borderRadius: '50px',
  backdropFilter: 'blur(10px)'
});

const PhotoGallery = ({ photos }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);

  if (!photos || photos.length === 0) return null;

  const displayedPhotos = photos.slice(0, 3);
  const remainingCount = photos.length - 3;

  const openLightbox = (index) => setSelectedIndex(index);
  const closeLightbox = () => setSelectedIndex(null);
  const nextPhoto = () => setSelectedIndex((prev) => (prev + 1) % photos.length);
  const prevPhoto = () => setSelectedIndex((prev) => (prev - 1 + photos.length) % photos.length);

  const openGalleryModal = () => setShowGalleryModal(true);
  const closeGalleryModal = () => setShowGalleryModal(false);

  const handleGalleryItemClick = (index) => {
    setShowGalleryModal(false);
    setTimeout(() => {
      setSelectedIndex(index);
    }, 100);
  };

  return (
    <SectionContainer id="gallery">
      <ContentWrapper>
        <SectionHeader>
          <SectionBadge>
            <BadgeText>Portfolio</BadgeText>
          </SectionBadge>
          <SectionTitle>
            Our <GradientText>Work</GradientText>
          </SectionTitle>
        </SectionHeader>

        <PhotosGrid>
          {displayedPhotos.map((photo, index) => (
            <PhotoCard key={photo.id || index} index={index} onClick={() => openLightbox(index)}>
              <PhotoImage
                src={photo.url || photo.image}
                alt={photo.alt || `Gallery image ${index + 1}`}
                loading="lazy"
              />
              <PhotoOverlay className="photo-overlay">
                <OverlayIcon>
                  <ZoomInIcon />
                </OverlayIcon>
                {photo.caption && <PhotoCaption>{photo.caption}</PhotoCaption>}
              </PhotoOverlay>
            </PhotoCard>
          ))}
        </PhotosGrid>

        {photos.length > 3 && (
          <ViewAllWrapper>
            <ViewAllButton onClick={openGalleryModal}>
              <GridViewIcon className="grid-icon" sx={{ fontSize: 20, color: '#d4af37', transition: 'color 0.3s' }} />
              <ViewAllText className="btn-text">View All Photos</ViewAllText>
              <PhotoCount>
                <CountText>+{remainingCount}</CountText>
              </PhotoCount>
              <ViewAllIcon className="btn-icon" />
            </ViewAllButton>
          </ViewAllWrapper>
        )}
      </ContentWrapper>

      {/* Gallery Modal - Shows all photos in grid */}
      {showGalleryModal && (
        <GalleryModalOverlay>
          <CloseButton onClick={closeGalleryModal}>
            <CloseIcon />
          </CloseButton>

          <GalleryModalContent>
            <GalleryModalHeader>
              <Box>
                <GalleryModalTitle>
                  Photo <span>Gallery</span>
                </GalleryModalTitle>
                <GalleryModalSubtitle>
                  {photos.length} photos in collection
                </GalleryModalSubtitle>
              </Box>
            </GalleryModalHeader>

            <GalleryGrid>
              {photos.map((photo, index) => (
                <GalleryItem
                  key={photo.id || index}
                  index={index}
                  onClick={() => handleGalleryItemClick(index)}
                >
                  <GalleryItemImage
                    src={photo.url || photo.image}
                    alt={photo.alt || `Gallery image ${index + 1}`}
                    loading="lazy"
                  />
                  <GalleryItemOverlay className="gallery-overlay">
                    <ZoomInIcon />
                  </GalleryItemOverlay>
                </GalleryItem>
              ))}
            </GalleryGrid>
          </GalleryModalContent>
        </GalleryModalOverlay>
      )}

      {/* Lightbox Modal - Single image view */}
      {selectedIndex !== null && (
        <LightboxModal onClick={closeLightbox}>
          <CloseButton onClick={closeLightbox}>
            <CloseIcon />
          </CloseButton>

          <NavButton
            sx={{ left: '28px' }}
            onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
          >
            <ChevronLeftIcon />
          </NavButton>

          <LightboxImage
            src={photos[selectedIndex]?.url || photos[selectedIndex]?.image}
            alt="Gallery"
            onClick={(e) => e.stopPropagation()}
          />

          <NavButton
            sx={{ right: '28px' }}
            onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
          >
            <ChevronRightIcon />
          </NavButton>

          <ImageCounter>
            {selectedIndex + 1} / {photos.length}
          </ImageCounter>
        </LightboxModal>
      )}
    </SectionContainer>
  );
};

export default PhotoGallery;
